import {
  type DayCode,
  type PloegSoortValue,
  alignRotationAnchor,
  dayCodeToPloegSoort,
  getShiftForDate,
} from './shift-roster'
import { clientWorkScheduleToDienstTijden } from './client-work-schedule-tijden'
import type { DienstUitzondering } from './dienst-uitzonderingen'
import { DEFAULT_DIENSTTIJDEN } from './werkbon-create-helpers'
import {
  type PauzeBron,
  type PauzeDienstType,
  type PauzeSysteemDefaults,
  type PauzeVerloning,
  dienstduurMinuten,
  resolvePauze,
} from './werkbon-pauze'

/**
 * FO-061 (batch-18) - Dienst-resolver voor het bijplannen van een losse dag.
 *
 * Pure functie: gegeven het rotatierooster van een `ClientWorkSchedule` (zoals de
 * werkbon-create-modal het al meekrijgt via `ClientWorkScheduleOptionDTO`) en een
 * kalenderdatum, leidt hij de geldende ploegdienst (ploegsoort + tijden) voor die
 * datum af - precies zoals de bulk-create-route op de server doet, zodat de
 * voorgevulde dienst in de UI overeenkomt met wat er daadwerkelijk gepland wordt.
 *
 * Bewust GEEN DB-toegang: welk werkschema voor een medewerker op een datum geldt,
 * bepaalt de aanroeper (de planner kiest de klant/het werkschema; bij het planbord
 * leiden we de klant af uit een naburige ploeg-werkbon). Deze functie vertaalt
 * uitsluitend "rooster + datum -> dienst".
 *
 * Fase-conventie (gelijk aan de server, `resolveOffset`/`rosterForOffset` met een
 * null-offset): het anker is `rotationStartDate`, uitgelijnd via
 * `alignRotationAnchor` (per-week-roosters snappen naar de maandag van hun
 * ISO-week; volcontinue roosters houden hun dag-anker). Daardoor geeft een losse
 * dag altijd de CANONIEKE rooster-fase van die datum, ongeacht in welke week hij
 * valt.
 *
 * Alle datums in UTC-kalenderdag (zoals de hele rooster-engine), wat de
 * Europe/Amsterdam-kalenderdatum representeert; ISO-weken (maandag-start) via
 * `alignRotationAnchor`.
 */

/**
 * Subset van een `ClientWorkSchedule` die de resolver nodig heeft. Structureel
 * compatibel met `ClientWorkScheduleOptionDTO` (`@streep/types`) aangevuld met de
 * per-datum-uitzonderingen, zodat de modal het geselecteerde werkschema 1:1 kan
 * doorgeven.
 */
export interface PloegRoosterInput {
  /** DayCode[] (`'V'|'L'|'N'|'D'|'X'`) bij 3/4/5-ploegen; null/leeg = geen rotatie. */
  rotationPattern: ReadonlyArray<string> | null
  /** Ankerdatum (ISO-string, bv. `2026-05-04` of een volledige ISO-timestamp). */
  rotationStartDate: string | null
  /** Cycluslengte (== pattern.length); null/0 = geen rotatie. */
  rotationCycleDays: number | null
  /**
   * `diensten`-JSON van het werkschema (`{ type, startTime, endTime, breakMinutes }[]`)
   * - bron van de tijden per ploegsoort. Onbekend/leeg -> `DEFAULT_DIENSTTIJDEN`.
   */
  diensten?: unknown
  /** Per-datum afwijkingen (gesloten / tijd-override). */
  dienstUitzonderingen?: ReadonlyArray<DienstUitzondering> | null
}

export interface ResolvedPloegdienst {
  ploegSoort: PloegSoortValue
  /** Starttijd HH:mm. */
  start: string
  /** Eindtijd HH:mm (kan < start zijn bij een nachtdienst). */
  eind: string
  pauzeMinutes: number
  /** FO-473 - geschreven of doorbetaald (zelfde cascade als de server). */
  pauzeVerloning: PauzeVerloning
  /** FO-473 - minuten pauze op de factuur (zelfde cascade als de server). */
  pauzeFacturatieMinuten: number
  pauzeBron: PauzeBron
  pauzeFacturatieBron: PauzeBron
}

/** FO-423/FO-473 — pauze-context die de aanroeper (UI/planbord) mee kan geven. */
export interface PloegdienstPauzeOpties {
  /** `ClientWorkSchedule.standaardPauzeMinuten` van de klant. */
  klantDefaultPauzeMinuten?: number | null
  /** `ClientWorkSchedule.standaardPauzeVerloning` van de klant. */
  klantDefaultVerloning?: PauzeVerloning | null
  /** `ClientWorkSchedule.standaardPauzeFacturatieMinuten` van de klant. */
  klantDefaultFacturatieMinuten?: number | null
  /** `ClientWorkSchedule.facturatiePauzeVanaf`: ingangsdatum van de factuurpauze-afspraken. */
  facturatieAfspraakVanaf?: string | null
  /** Diensttype van de (te maken) werkbon; bepaalt de systeemdefault. */
  dienstType?: PauzeDienstType | null
  /** Instelbare systeemdefaults (dagdienst/ploegen). */
  systeemDefaults?: Partial<PauzeSysteemDefaults> | null
}

/**
 * FO-423 — geen eigen pauze-default meer. Ontbreekt de pauze op de dienst, dan
 * bepaalt de GEDEELDE cascade (`resolvePauze`: klant-default →
 * systeemdefault per diensttype) 'm, exact zoals de server bij het aanmaken.
 * Een tweede afleiding hier zou de UI-preview weer laten afwijken van wat er
 * daadwerkelijk gepland wordt.
 */

const DAY_CODES: ReadonlyArray<DayCode> = ['V', 'L', 'N', 'D', 'X']

/** Parse `yyyy-mm-dd` (evt. met tijd-suffix) als UTC-kalenderdag; null bij ongeldig. */
function parseUtcDay(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!m) return null
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return Number.isNaN(d.getTime()) ? null : d
}

/** Cast een string[]-pattern naar DayCode[]; null als er een onbekende code in zit. */
function toDayCodePattern(pattern: ReadonlyArray<string>): DayCode[] | null {
  const out: DayCode[] = []
  for (const code of pattern) {
    if (!(DAY_CODES as ReadonlyArray<string>).includes(code)) return null
    out.push(code as DayCode)
  }
  return out
}

/**
 * Leidt de geldende ploegdienst voor `datumYmd` af uit het rotatierooster.
 *
 * Geeft `null` (= handmatige dienstkeuze) wanneer:
 *  - er geen (volledig) rotatierooster is (`rotationPattern`/`rotationStartDate`/
 *    `rotationCycleDays` ontbreekt of inconsistent);
 *  - de datum ongeldig is;
 *  - het rooster die dag een rustdag (`X`) geeft;
 *  - een per-datum-uitzondering die dag op `gesloten` zet.
 */
export function resolvePloegdienstVoorDatum(
  rooster: PloegRoosterInput,
  datumYmd: string,
  pauzeOpties?: PloegdienstPauzeOpties,
): ResolvedPloegdienst | null {
  const { rotationPattern, rotationStartDate, rotationCycleDays } = rooster
  // Geen (volledig) rotatierooster -> geen automatische dienst.
  if (
    !rotationPattern ||
    rotationPattern.length === 0 ||
    !rotationStartDate ||
    !rotationCycleDays ||
    rotationCycleDays <= 0 ||
    rotationPattern.length !== rotationCycleDays
  ) {
    return null
  }

  const datum = parseUtcDay(datumYmd)
  const anker = parseUtcDay(rotationStartDate)
  if (!datum || !anker) return null
  const pattern = toDayCodePattern(rotationPattern)
  if (!pattern) return null

  // Per-datum-uitzondering: `gesloten` -> die dag heeft geen reguliere dienst.
  const datumKey = datumYmd.slice(0, 10)
  const uitzondering = (rooster.dienstUitzonderingen ?? []).find((u) => u.datum === datumKey)
  if (uitzondering?.gesloten) return null

  const roster = {
    pattern,
    startDate: alignRotationAnchor(anker, rotationCycleDays),
    cycleDays: rotationCycleDays,
  }
  const ploegSoort = dayCodeToPloegSoort(getShiftForDate(roster, datum))
  if (!ploegSoort) return null // X = rustdag -> handmatige keuze

  // Tijden: werkschema-diensten zijn de bron (een waarheid); val terug op de
  // app-defaults. Een per-datum tijd-/pauze-uitzondering overschrijft per veld.
  const scheduleTijden = clientWorkScheduleToDienstTijden(rooster.diensten ?? [])
  const basis = scheduleTijden?.[ploegSoort]
  const start = uitzondering?.startTime ?? basis?.start ?? DEFAULT_DIENSTTIJDEN[ploegSoort].start
  const eind = uitzondering?.endTime ?? basis?.eind ?? DEFAULT_DIENSTTIJDEN[ploegSoort].eind
  // FO-423/FO-473 — dezelfde cascade als de server, op beide assen:
  // uitzondering → dienst → korte dienst → klant-default → systeemdefault.
  const pauze = resolvePauze({
    uitzonderingPauzeMinuten: uitzondering?.breakMinutes ?? null,
    uitzonderingVerloning: uitzondering?.pauzeVerloning ?? null,
    uitzonderingFacturatieMinuten: uitzondering?.pauzeFacturatieMinuten ?? null,
    dienstPauzeMinuten: basis?.pauzeMinutes ?? null,
    dienstVerloning: basis?.pauzeVerloning ?? null,
    dienstFacturatieMinuten: basis?.pauzeFacturatieMinuten ?? null,
    klantDefaultPauzeMinuten: pauzeOpties?.klantDefaultPauzeMinuten ?? null,
    klantDefaultVerloning: pauzeOpties?.klantDefaultVerloning ?? null,
    klantDefaultFacturatieMinuten: pauzeOpties?.klantDefaultFacturatieMinuten ?? null,
    facturatieAfspraakVanaf: pauzeOpties?.facturatieAfspraakVanaf ?? null,
    datum: datumKey,
    dienstType: pauzeOpties?.dienstType ?? null,
    ploegSoort,
    dienstduurMinuten: dienstduurMinuten(start, eind),
    systeemDefaults: pauzeOpties?.systeemDefaults ?? null,
  })
  return {
    ploegSoort,
    start,
    eind,
    pauzeMinutes: pauze.minuten,
    pauzeVerloning: pauze.verloning,
    pauzeFacturatieMinuten: pauze.facturatieMinuten,
    pauzeBron: pauze.bron,
    pauzeFacturatieBron: pauze.facturatieBron,
  }
}
